import React, { useEffect, useRef } from 'react';
import {
  Plus,
  Copy,
  Trash2,
  ChevronUp,
  ChevronDown,
  Download,
  Upload,
  Link,
  Pencil,
  ArrowLeftRight,
} from 'lucide-react';
import { getComponentDefinition } from '../../componentDefinitions';

/**
 * LayersContextMenu - Right-click context menu for tree nodes
 *
 * Actions:
 * - Add Child (for containers)
 * - Move Up/Down
 * - Duplicate
 * - Delete
 */
export default function LayersContextMenu({
  x,
  y,
  type, // 'section' | 'component'
  section,
  sectionIndex,
  component,
  componentPath,
  onClose,
  handleDeleteSection,
  handleQuickUpdate,
  onShowComponentPalette,
  onExportSection,
  onImportSection,
  page,
  onSaveAsSiteSection,
  onRenameSection,
  onRenameComponent,
}) {
  const menuRef = useRef(null);

  // Close on click outside or Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (x + rect.width > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 8;
      }
      if (y + rect.height > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 8;
      }

      menuRef.current.style.left = `${adjustedX}px`;
      menuRef.current.style.top = `${adjustedY}px`;
    }
  }, [x, y]);

  // Determine available actions based on type
  const isLayoutSection = type === 'section' && section?.section_type === 'layout-1';
  const definition = type === 'component' ? getComponentDefinition(component?.type) : null;
  const canHaveChildren = isLayoutSection || (type === 'component' && definition?.canHaveChildren === true);

  const handleAddChild = () => {
    if (onShowComponentPalette && section) {
      if (isLayoutSection) {
        // Add row to section root
        onShowComponentPalette(section.id, 'root', section.content?.components?.length || 0);
      } else if (type === 'component' && componentPath) {
        // Add child to row/column
        onShowComponentPalette(section.id, componentPath, component?.children?.length || 0);
      }
    }
    onClose();
  };

  const handleDuplicate = () => {
    if (type === 'section') {
      // Section duplication would need to be implemented
      console.log('Duplicate section:', section?.id);
    } else if (type === 'component' && handleQuickUpdate && section) {
      // Use handleQuickUpdate with layoutAction to duplicate component
      handleQuickUpdate(section.id, {
        layoutAction: 'duplicateComponent',
        componentPath,
        component,
      });
    }
    onClose();
  };

  const handleDelete = () => {
    if (type === 'section' && section) {
      handleDeleteSection?.(section.id);
    } else if (type === 'component' && handleQuickUpdate && section) {
      // Use handleQuickUpdate with layoutAction to remove component
      handleQuickUpdate(section.id, {
        layoutAction: 'removeComponent',
        componentPath,
      });
    }
    onClose();
  };

  const handleMoveUp = () => {
    if (type === 'component' && handleQuickUpdate && section) {
      handleQuickUpdate(section.id, {
        layoutAction: 'moveUp',
        componentPath,
      });
    }
    onClose();
  };

  const handleMoveDown = () => {
    if (type === 'component' && handleQuickUpdate && section) {
      handleQuickUpdate(section.id, {
        layoutAction: 'moveDown',
        componentPath,
      });
    }
    onClose();
  };

  const handleExportSection = () => {
    if (type === 'section' && section && onExportSection) {
      onExportSection(section);
    }
    onClose();
  };

  const handleImportSection = () => {
    if (onImportSection) {
      onImportSection();
    }
    onClose();
  };

  const handleRenameSection = () => {
    if (type === 'section' && section && onRenameSection) {
      onRenameSection(section);
    }
    onClose();
  };

  const handleRenameComponent = () => {
    if (type === 'component' && section && component && componentPath && onRenameComponent) {
      onRenameComponent(section.id, componentPath, component);
    }
    onClose();
  };

  const handleSaveAsSiteSection = () => {
    if (type === 'section' && section && onSaveAsSiteSection) {
      onSaveAsSiteSection(section);
    }
    onClose();
  };

  const isConvertible = type === 'component' && (component?.type === 'container' || component?.type === 'block');
  const convertLabel = component?.type === 'container' ? 'Convert to Block' : 'Convert to Container';

  const handleConvertComponent = () => {
    if (type === 'component' && handleQuickUpdate && section && component) {
      const targetType = component.type === 'container' ? 'block' : 'container';
      handleQuickUpdate(section.id, {
        layoutAction: 'convertComponent',
        componentPath,
        targetType,
      });
    }
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[160px]"
      style={{ left: x, top: y }}
    >
      {/* Add Child (for containers) */}
      {canHaveChildren && (
        <>
          <MenuItem
            icon={Plus}
            label={
              isLayoutSection ? 'Add Row' :
              component?.type === 'row' ? 'Add Column' :
              component?.type === 'column' || component?.type === 'container' || component?.type === 'block' ? 'Add Component' : 'Add'
            }
            onClick={handleAddChild}
          />
          <Divider />
        </>
      )}

      {/* Rename */}
      {(type === 'section' || type === 'component') && (
        <>
          <MenuItem
            icon={Pencil}
            label="Rename"
            onClick={type === 'section' ? handleRenameSection : handleRenameComponent}
          />
          <Divider />
        </>
      )}

      {/* Move actions (for components only) */}
      {type === 'component' && (
        <>
          <MenuItem
            icon={ChevronUp}
            label="Move Up"
            onClick={handleMoveUp}
          />
          <MenuItem
            icon={ChevronDown}
            label="Move Down"
            onClick={handleMoveDown}
          />
          <Divider />
        </>
      )}

      {/* Duplicate */}
      <MenuItem
        icon={Copy}
        label="Duplicate"
        onClick={handleDuplicate}
      />

      {/* Convert Container ↔ Block */}
      {isConvertible && (
        <MenuItem
          icon={ArrowLeftRight}
          label={convertLabel}
          onClick={handleConvertComponent}
        />
      )}

      {/* Export/Import (sections only) */}
      {type === 'section' && (
        <>
          <Divider />
          <MenuItem
            icon={Download}
            label="Export Section"
            onClick={handleExportSection}
          />
          <MenuItem
            icon={Upload}
            label="Import Section"
            onClick={handleImportSection}
          />
          {page?.site_id && (
            <MenuItem
              icon={Link}
              label="Save as Site Section"
              onClick={handleSaveAsSiteSection}
            />
          )}
        </>
      )}

      {/* Delete */}
      <MenuItem
        icon={Trash2}
        label="Delete"
        onClick={handleDelete}
        danger
      />
    </div>
  );
}

/**
 * Menu item component
 */
function MenuItem({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs
        transition-colors
        ${danger
          ? 'text-red-600 hover:bg-red-50'
          : 'text-gray-700 hover:bg-gray-50'
        }
      `}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

/**
 * Divider component
 */
function Divider() {
  return <div className="border-t border-gray-100 my-1" />;
}
